import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import settingsService from '../../../services/settingsService';
import NotificationToast from '../../../components/ui/NotificationToast';

const SkillLevelsMaster = () => {
  const [skillLevels, setSkillLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    order: 0
  });

  useEffect(() => {
    loadSkillLevels();
  }, []);

  const loadSkillLevels = async () => {
    try {
      setLoading(true);
      const result = await settingsService.getSkillLevels();
      if (result?.success) {
        setSkillLevels(result.data || []);
      }
    } catch (error) {
      console.log('Error loading skill levels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEdit = (skillLevel) => {
    setEditingId(skillLevel.id);
    setFormData({
      name: skillLevel.name || '',
      description: skillLevel.description || '',
      order: skillLevel.order || 0
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', description: '', order: 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = editingId
        ? await settingsService.updateSkillLevel(editingId, formData)
        : await settingsService.createSkillLevel(formData);

      if (result?.success) {
        setNotification({ 
          message: `Skill level ${editingId ? 'updated' : 'created'} successfully`, 
          type: 'success' 
        });
        handleCancel();
        loadSkillLevels();
      } else {
        setNotification({ 
          message: result?.error || `Failed to ${editingId ? 'update' : 'create'} skill level`, 
          type: 'error' 
        });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
      console.log('Error saving skill level:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this skill level?')) {
      return;
    }

    try {
      const result = await settingsService.deleteSkillLevel(id);
      if (result?.success) {
        setNotification({ message: 'Skill level deleted successfully', type: 'success' });
        loadSkillLevels();
      } else {
        setNotification({ message: result?.error || 'Failed to delete skill level', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
      console.log('Error deleting skill level:', error);
    }
  };

  return (
    <div>
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Skill Levels</h4>
          <p className="text-sm text-muted-foreground">Manage skill level options for leads and candidates</p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
            <Icon name="Plus" size={16} />
            Add Skill Level
          </Button>
        )}
      </div>

      {showForm && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <h5 className="font-semibold text-foreground mb-4">
            {editingId ? 'Edit Skill Level' : 'Add New Skill Level'}
          </h5>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Level Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Entry, Intermediate, Senior, Expert"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Display Order
                </label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 0)}
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <Input
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" className="flex items-center gap-2">
                <Icon name="Save" size={16} />
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading skill levels...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {skillLevels.length === 0 ? (
            <div className="col-span-full text-center py-8 text-muted-foreground">
              No skill levels found. Click "Add Skill Level" to create one.
            </div>
          ) : (
            [...skillLevels].sort((a, b) => (a.order || 0) - (b.order || 0)).map(skillLevel => (
              <div key={skillLevel.id} className="p-4 border border-border rounded-lg hover:bg-muted/50">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h5 className="font-semibold text-foreground">{skillLevel.name}</h5>
                    {skillLevel.description && (
                      <p className="text-sm text-muted-foreground mt-1">{skillLevel.description}</p>
                    )}
                    {skillLevel.order !== null && (
                      <p className="text-xs text-muted-foreground mt-1">Order: {skillLevel.order}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(skillLevel)}
                      className="h-8 w-8 p-0"
                      title="Edit"
                    >
                      <Icon name="Edit" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(skillLevel.id)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SkillLevelsMaster;

